import { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { Preset, FoodRelation } from '../lib/supabase';
import { createPreset, updatePreset, duplicatePreset } from '../lib/api';

interface PresetEditorProps {
  preset: Preset | null;
  onSave: () => void;
  onCancel: () => void;
}

export function PresetEditor({ preset, onSave, onCancel }: PresetEditorProps) {
  const isBuiltIn = preset?.owner_user_id === null;
  const [name, setName] = useState('');
  const [tag, setTag] = useState('');
  const [foodRelation, setFoodRelation] = useState<FoodRelation>('ANY');
  const [priority, setPriority] = useState(1);
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (preset) {
      setName(preset.name);
      setTag(preset.tag);
      setFoodRelation(preset.food_relation);
      setPriority(preset.priority);
      setDescription(preset.description || '');
    }
  }, [preset]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isBuiltIn) {
        await duplicatePreset(preset.id);
      } else if (preset) {
        await updatePreset(preset.id, {
          name,
          tag,
          food_relation: foodRelation,
          priority,
          description: description || null,
        });
      } else {
        const { data: user } = await (await import('../lib/supabase')).supabase.auth.getUser();
        await createPreset({
          owner_user_id: user.user!.id,
          name,
          tag,
          food_relation: foodRelation,
          priority,
          description: description || null,
        });
      }
      onSave();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto p-4">
      <div className="bg-white rounded-2xl shadow-xl p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-gray-900">
            {isBuiltIn ? 'Duplicate Preset' : preset ? 'Edit Preset' : 'New Preset'}
          </h2>
          <button
            onClick={onCancel}
            className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg"
          >
            <X size={24} />
          </button>
        </div>

        {isBuiltIn && (
          <div className="bg-blue-50 border border-blue-200 text-blue-800 px-4 py-3 rounded-lg mb-4 text-sm">
            This will create a copy in your presets. The original will remain unchanged.
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Name
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="e.g., Vitamin D3"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tag
            </label>
            <input
              type="text"
              value={tag}
              onChange={(e) => setTag(e.target.value.toLowerCase().replace(/\s+/g, '-'))}
              required
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="e.g., vitamin-d"
            />
            <p className="text-xs text-gray-500 mt-1">
              Used for gap rules. Lowercase with dashes.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Food Relation
            </label>
            <select
              value={foodRelation}
              onChange={(e) => setFoodRelation(e.target.value as FoodRelation)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="ANY">Any time</option>
              <option value="BEFORE_30M">30 minutes before food</option>
              <option value="WITH_MEAL">With meal</option>
              <option value="AWAY_120M">2 hours away from food</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Priority (1-5, 1 = highest)
            </label>
            <input
              type="number"
              min="1"
              max="5"
              value={priority}
              onChange={(e) => setPriority(parseInt(e.target.value))}
              required
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description (optional)
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="e.g., Supports bone health and immune function"
            />
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onCancel}
              className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50"
            >
              {loading ? 'Saving...' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
